import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { classNames, visibilityBadgeClass, visibilityLabel } from './helpers';

const defaultState = {
  title: '',
  description: '',
  tags: '',
  is_private: false,
};

const AttachmentEditor = ({ attachment, open, onClose, onSave, busy }) => {
  const [form, setForm] = useState(defaultState);

  const headerOffsetPx = useMemo(() => {
    // Dev tools pages run inside a layout with a fixed header.
    // Keep a conservative offset so the drawer isn't clipped at the top.
    return 60;
  }, []);

  useEffect(() => {
    if (!attachment) {
      setForm(defaultState);
      return;
    }
    setForm({
      title: attachment.title || '',
      description: attachment.description || '',
      tags: attachment.tags_string || attachment.tags_list?.join(',') || '',
      is_private: !!attachment.is_private,
    });
  }, [attachment]);

  if (!open || !attachment) {
    return null;
  }

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const submit = (e) => {
    e.preventDefault();
    onSave({ ...form });
  };

  return (
    <div
      className="z-40"
      style={{ position: 'fixed', top: headerOffsetPx, left: 0, right: 0, bottom: 0 }}
    >
      <div
        onClick={onClose}
        style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: '#000', opacity: 0.4 }}
      />
      <div
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          width: '100%',
          maxWidth: 420,
          backgroundColor: '#fff',
          overflowY: 'auto',
          boxShadow: '0 10px 25px rgba(0,0,0,0.25)',
        }}
      >
        <div className="clearfix" style={{ padding: '12px 15px', borderBottom: '1px solid #eee' }}>
          <div className="pull-left">
            <h4 className="m-0">Edit Attachment</h4>
            <div className="text-muted" style={{ fontSize: 12 }}>ID #{attachment.id}</div>
          </div>
          <div className="pull-right">
            <button type="button" className="btn btn-link btn-xs" onClick={onClose}>Close</button>
          </div>
        </div>

        <form onSubmit={submit} style={{ padding: 15 }}>
          <div className="form-group">
            <label className="control-label">Title</label>
            <input
              type="text"
              className="form-control input-sm"
              value={form.title}
              onChange={(e) => handleChange('title', e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label className="control-label">Description</label>
            <textarea
              className="form-control input-sm"
              rows={3}
              value={form.description}
              onChange={(e) => handleChange('description', e.target.value)}
            />
          </div>

          <div className="form-group">
            <label className="control-label">Tags (comma separated)</label>
            <input
              type="text"
              className="form-control input-sm"
              value={form.tags}
              onChange={(e) => handleChange('tags', e.target.value)}
            />
          </div>

          <div className="form-group">
            <label className="control-label">Visibility</label>
            <div style={{ marginTop: 6 }}>
              <span
                className={classNames('label', attachment?.is_private ? 'label-danger' : 'label-success')}
              >
                {visibilityLabel(attachment)}
              </span>
              <div className="text-muted" style={{ fontSize: 12, marginTop: 6 }}>
                Visibility is set at upload time and cannot be changed here.
              </div>
            </div>
          </div>

          <div className="clearfix" style={{ paddingTop: 5 }}>
            <div className="pull-left">
              <button type="button" className="btn btn-default btn-xs" onClick={onClose} disabled={busy}>Cancel</button>
            </div>
            <div className="pull-right">
              <button type="submit" className={classNames('btn btn-primary btn-xs', busy && 'disabled')} disabled={busy}>
                {busy ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

AttachmentEditor.propTypes = {
  attachment: PropTypes.object,
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  busy: PropTypes.bool,
};

AttachmentEditor.defaultProps = {
  attachment: null,
  open: false,
  busy: false,
};

export default AttachmentEditor;
